/**
 * "Hat die Optimierung diese Komponente angefasst?" (Spec 2026-09-15, 2.1)
 *
 * Quelle ist AUSSCHLIESSLICH der Vergleich zweier gespeicherter Werte: in der
 * Karte `product_scores` pre gegen post, im Feed-Check die Feed-Mittel zweier
 * Befunde. Es wird nichts neu gescort und keine Produktspalte gelesen.
 *
 * Vier Aussagen, eine Schwelle: 70 ist die "gut"-Grenze der Ampel
 * (`score-colors.ts`) und der Sammelfreigabe (`workbench_bulk_summary`).
 * Keine dritte Grenze.
 *
 * `unchanged_low` heisst "nicht angefasst": der Wert kommt aus dem Feed des
 * Haendlers oder aus Pass 2, und die Datenbank haelt nicht fest, welches von
 * beiden (Sprach-/Plan-Gate oder leere Lieferung). Deshalb nennt der Text
 * beide Quellen; eine genauere Aussage ist Session 6.
 */
import type { WorkbenchComponentScores } from '@/hooks/useWorkbench';

export type TouchState = 'improved' | 'unchanged_full' | 'unchanged_low' | 'worse' | 'not_applicable' | 'unknown';

export const TOUCH_GOOD_THRESHOLD = 70;

/**
 * @param tolerance Feed-Check: 0,5 Punkte (numeric-Rundung der Spaltenmittel,
 *   dieselbe wie INCONSISTENCY_TOLERANCE). Karte: 0, die Werte sind integer.
 */
export function touchState(
  pre: number | null | undefined,
  post: number,
  tolerance = 0,
): TouchState {
  if (pre === null || pre === undefined || !Number.isFinite(pre)) return 'unknown';
  const delta = post - pre;
  if (delta > tolerance) return 'improved';
  if (delta < -tolerance) return 'worse';
  return post >= TOUCH_GOOD_THRESHOLD ? 'unchanged_full' : 'unchanged_low';
}

export function componentTouchState(
  pre: WorkbenchComponentScores | null,
  post: WorkbenchComponentScores,
  key: keyof WorkbenchComponentScores,
  attributeApplicable: boolean,
): TouchState {
  if (key === 'attributes' && !attributeApplicable) return 'not_applicable';
  if (!pre) return 'unknown';
  return touchState(pre[key], post[key]);
}

/**
 * Ende des Deploys der fuenf Score-Functions mit v3.1 (2026-09-18 00:05:56
 * UTC, auf die volle Minute aufgerundet).
 *
 * WOZU: seit v3.1 zaehlt die GPC-Komponente die TIEFE der Google-Kategorie
 * (Ebene 1 bis 2 gibt 40, Ebene 3 gibt 70, ab Ebene 4 volle 100). Jede Zeile
 * davor traegt den binaeren Vorgaenger: 100 fuer jede gesetzte Kategorie, 0
 * ohne. Eine pre-Zeile aus der alten Epoche gegen eine post-Zeile aus der
 * neuen zu halten meldet einen Rueckgang von 100 auf 40, ohne dass sich an
 * der Kategorie etwas geaendert haette -- die Karte behauptete dann "Nach der
 * Optimierung niedriger als vorher" ueber einen reinen Formelwechsel.
 *
 * WARUM UEBER DEN ZEITSTEMPEL UND NICHT UEBER EINE VERSIONSSPALTE:
 * `product_scores` hat keine `scoring_version` (anders als
 * `feed_health_snapshots`, wo `baselineSnapshot.ts` genau damit arbeitet).
 * Eine Spalte nachzuruesten ist eine Migration und damit eine eigene
 * Entscheidung; sie liegt als Folgeentscheidung in
 * `docs/entscheidung-vorlage-rescore-bestand.md`. Bis dahin ist der
 * Rechenzeitpunkt das einzige Merkmal, das eine Zeile mitbringt.
 */
export const GPC_DEPTH_SCORING_SINCE = '2026-09-18T00:06:00Z';

/**
 * Duerfen die GPC-Werte dieser beiden Score-Zeilen gegeneinander gehalten
 * werden?
 *
 * Nur, wenn BEIDE nachweislich in der Tiefen-Epoche gerechnet wurden. Ein
 * fehlender oder unlesbarer Zeitstempel heisst "wir wissen es nicht", und das
 * faellt hier bewusst auf `false`: ein falsch behaupteter Rueckgang ist
 * teurer als eine ausgelassene Aussage.
 */
export function gpcEpochComparable(
  preCalculatedAt: string | null | undefined,
  postCalculatedAt: string | null | undefined,
): boolean {
  const seit = Date.parse(GPC_DEPTH_SCORING_SINCE);
  const werte = [preCalculatedAt, postCalculatedAt].map((v) => (v ? Date.parse(v) : Number.NaN));
  return werte.every((v) => Number.isFinite(v) && v >= seit);
}

/** Nur die zwei Faelle, die eine Erklaerung brauchen (Spec 2.2). */
export function needsTouchText(state: TouchState): boolean {
  return state === 'unchanged_low' || state === 'worse';
}
